import { User } from '@/models/Users'
import { OrgData } from '@/models/Orgs'
import { UUID } from 'crypto'

/**
 * Checks if organisation creation is disabled based on environment variable
 * @returns boolean indicating if org creation is disabled
 */
export const isOrgCreationDisabled = (): boolean => {
  return import.meta.env.VITE_DISABLE_ORG_CREATION === 'true'
}

/**
 * Determines the effective organisation UUID for a given user.
 *
 * @param user - The user object, which may be null. It may contain a selected organisation UUID.
 * @param orgs - An array of available organisation UUIDs, which may be null or empty.
 * @returns The user's selected organisation UUID if it exists and is included in the provided list of organisations.
 *          If the user has not selected an organisation or the selected UUID is not in the list, returns the first UUID in the list.
 *          If the list of organisations is null or empty, returns an empty UUID.
 */
export const getEffectiveOrgUuid = (
  user: User | null,
  orgs: UUID[] | null
): UUID => {
  if (!orgs || orgs.length === 0) {
    return '' as UUID
  }

  if (!user?.selected_org_uuid || !orgs.includes(user.selected_org_uuid)) {
    return orgs[0]
  }

  return user.selected_org_uuid
}

/**
 * Returns true if a URL has no path beyond the root (e.g. https://example.com or https://example.com/)
 */
export const isRootUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url)
    return parsed.pathname === '/' || parsed.pathname === ''
  } catch {
    return false
  }
}

/**
 * Returns a safe http(s) href for an externally-sourced link (e.g. an
 * LLM-extracted application_link), or null if it can't be rendered as one.
 * A schemeless value (e.g. "www.funder.org.nz/apply") is treated as an
 * implied https:// link rather than a relative in-app route, matching how
 * the backend's own reference-URL check (funder_reference_urls.py) treats
 * these same fields. Anything with an explicit non-http(s) scheme
 * (javascript:, data:, etc.) is rejected outright rather than rendered.
 */
export const getSafeExternalHref = (link: string): string | null => {
  const trimmed = link.trim()
  if (!trimmed) return null
  const schemeMatch = trimmed.match(/^([a-zA-Z][a-zA-Z\d+\-.]*):/)
  if (!schemeMatch) return `https://${trimmed}`
  const scheme = schemeMatch[1].toLowerCase()
  return scheme === 'http' || scheme === 'https' ? trimmed : null
}

/**
 * Checks if an organisation is on a free tier
 * @param org - The organisation data object
 * @returns boolean indicating if the organisation is on a free tier
 */
export const isFreeTier = (org: OrgData | null): boolean => {
  if (!org) return true

  // Check if membership is 'free' (case insensitive)
  if (!org.membership) return true
  if (org.membership?.toLowerCase() === 'free') return true

  return false
}

/**
 * Gets the number of scans performed for a specific funding need
 * @param scanIds - Array of scan IDs for the funding need
 * @returns number of scans performed
 */
export const getNumberOfScans = (scanIds: number[] | undefined): number => {
  if (!scanIds) return 0
  return scanIds.length
}

/**
 * Checks if an org has reached its (org-wide) free-tier scan limit.
 * @param totalScansUsed - distinct scans the org has run across all funding needs
 * @param maxTotalScans - the org's scan allowance; negative means unlimited
 * @returns boolean indicating if the scan limit is reached
 */
export const hasFreeUserReachedScanLimit = (
  totalScansUsed: number | undefined,
  maxTotalScans: number | undefined,
  isFreeTierOrg: boolean
): boolean => {
  // Only Free tier is capped, matching the backend, which returns early for any
  // membership != 'Free' (app/api/client/scans.py). orgs.max_total_scans was
  // added NOT NULL DEFAULT 1 and no upgrade path clears it, so most Paid orgs
  // still carry a numeric cap they are long past. Honouring that number here
  // disabled the scan controls for paying customers the server would have let
  // scan — hence the membership check, not just the number.
  if (!isFreeTierOrg) return false
  // No cap configured (absent), or negative = unlimited: nothing to reach.
  if (maxTotalScans === undefined || maxTotalScans < 0) return false
  // A cap applies but the usage count is unknown (still loading, or /org-usage
  // errored). Guessing "under cap" is the unsafe way to be wrong: it hides a
  // capped org's existing results behind a re-scan prompt the backend then
  // 402s, leaving them no way back to their matches. Assume the cap is reached
  // so they keep their results with a staleness warning instead.
  if (totalScansUsed === undefined) return true
  return totalScansUsed >= maxTotalScans
}

/**
 * Determines whether a free-tier org should be blocked from drafting another
 * application, mirroring the backend application paywall. A negative
 * maxTotalApplications (e.g. -1) means unlimited.
 * @param isUserFreeTier - Whether the org is on a free tier
 * @param totalApplications - The org's current total application count
 * @param maxTotalApplications - The org's allowed application limit (-1 = unlimited)
 * @returns 'upgrade' when the org should be prompted to upgrade, otherwise undefined
 */
export const getDraftRestriction = (
  isUserFreeTier: boolean,
  totalApplications: number,
  maxTotalApplications: number
): 'upgrade' | undefined => {
  if (!isUserFreeTier) return undefined
  if (maxTotalApplications < 0) return undefined
  return totalApplications >= maxTotalApplications ? 'upgrade' : undefined
}

/**
 * Determines whether a free-tier org should be blocked from adding another
 * funding request. Only non-archived requests count: an archived one cannot be
 * scanned, so letting it hold the single free-tier slot leaves an org whose
 * only request was archived with no way to make a usable one.
 *
 * Reaching the form is not a way past the paywall. The scan allowance is the
 * binding limit and archiving never refunds it, which
 * _enforce_free_tier_scan_limit enforces server-side.
 * @param isUserFreeTier - Whether the org is on a free tier
 * @param liveFundingNeedCount - The org's count of non-archived funding requests
 * @returns true when the org should be prompted to upgrade instead of shown the form
 */
export const shouldBlockSecondNeed = (
  isUserFreeTier: boolean,
  liveFundingNeedCount: number
): boolean => isUserFreeTier && liveFundingNeedCount > 0

/**
 * Limits the results array to a specified number of items for free tier users
 * @param results - Array of results to limit
 * @param isFree - Whether the user is on a free tier
 * @param limit - Maximum number of results to show for free users
 * @returns Limited array of results for free users, or original array for paid users
 */
export const limitResultsForFreeTier = <T>(
  results: T[],
  isFree: boolean,
  limit: number = 5
): T[] => {
  if (!isFree || !results) return results
  return results.slice(0, limit)
}
